import { useState, useEffect, useCallback, useRef } from 'react';
import {
    buildSearchStorageKey,
    readPersistedSearchState,
    savePersistedSearchState,
    clearPersistedSearchState,
} from '../utils/searchStatePersistence';

/**
 * Persistance sessionStorage des filtres / pagination (pattern Check Claims).
 */
const usePersistedSearchState = (pageKey, emptyFilters, options = {}) => {
    const {
        defaultPage = 1,
        defaultItemsPerPage = 10,
        mutableItemsPerPage = false,
        withAppliedFilters = true,
        withCompanyPartnerSearch = false,
        persistWhen = true,
    } = options;

    const storageKey = buildSearchStorageKey(pageKey);
    const initialRef = useRef(null);
    if (initialRef.current === null) {
        initialRef.current = readPersistedSearchState(storageKey, emptyFilters, {
            defaultPage,
            defaultItemsPerPage,
            withCompanyPartnerSearch,
        }) || {
            filters: { ...emptyFilters },
            appliedFilters: withAppliedFilters ? {} : undefined,
            currentPage: defaultPage,
            itemsPerPage: defaultItemsPerPage,
            companyPartnerSearch: withCompanyPartnerSearch ? '' : undefined,
        };
    }
    const initial = initialRef.current;

    const [currentPage, setCurrentPage] = useState(initial.currentPage ?? defaultPage);
    const [itemsPerPage, setItemsPerPageState] = useState(initial.itemsPerPage ?? defaultItemsPerPage);
    const [filters, setFilters] = useState(initial.filters ?? { ...emptyFilters });
    const [appliedFilters, setAppliedFilters] = useState(
        withAppliedFilters ? (initial.appliedFilters ?? {}) : undefined
    );
    const [companyPartnerSearch, setCompanyPartnerSearch] = useState(
        withCompanyPartnerSearch ? (initial.companyPartnerSearch ?? '') : undefined
    );

    useEffect(() => {
        if (!persistWhen) return;
        const payload = {
            filters,
            currentPage,
            itemsPerPage,
        };
        if (withAppliedFilters) {
            payload.appliedFilters = appliedFilters;
        }
        if (withCompanyPartnerSearch) {
            payload.companyPartnerSearch = companyPartnerSearch;
        }
        savePersistedSearchState(storageKey, payload);
    }, [
        persistWhen,
        storageKey,
        filters,
        appliedFilters,
        currentPage,
        itemsPerPage,
        companyPartnerSearch,
        withAppliedFilters,
        withCompanyPartnerSearch,
    ]);

    const clearSearchState = useCallback(() => {
        clearPersistedSearchState(storageKey);
        const empty = { ...emptyFilters };
        setFilters(empty);
        if (withAppliedFilters) {
            setAppliedFilters(empty);
        }
        setCurrentPage(defaultPage);
        if (mutableItemsPerPage) {
            setItemsPerPageState(defaultItemsPerPage);
        }
        if (withCompanyPartnerSearch) {
            setCompanyPartnerSearch('');
        }
    }, [
        storageKey,
        emptyFilters,
        defaultPage,
        defaultItemsPerPage,
        mutableItemsPerPage,
        withAppliedFilters,
        withCompanyPartnerSearch,
    ]);

    const result = {
        storageKey,
        filters,
        setFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        clearSearchState,
    };

    if (mutableItemsPerPage) {
        result.setItemsPerPage = setItemsPerPageState;
    }
    if (withAppliedFilters) {
        result.appliedFilters = appliedFilters;
        result.setAppliedFilters = setAppliedFilters;
    }
    if (withCompanyPartnerSearch) {
        result.companyPartnerSearch = companyPartnerSearch;
        result.setCompanyPartnerSearch = setCompanyPartnerSearch;
    }

    return result;
};

export default usePersistedSearchState;
